import React, { useEffect, useState } from 'react';
import { Loader, StepperControls, StepWrapper, StyledCardSection } from '@fnol/library/src/components';
import {
    GenericObject,
    ScreenProps,
    acceptedUploadFileTypes,
    addFilesToDocumentList,
    common,
    prepareDocumentUploadFormData,
    totalUploadSize,
} from '@fnol/library/src/lib';
import { translations } from '../../lib/consts';
import { useApi } from '@fnol/library/src/hooks';
import { getDocList, submitDocuments } from '../../rpc/actions';
import { useFormikContext } from 'formik';
import { FormHelperText, Typography } from '@mui/material';
import DocumentGroup from '@fnol/library/src/components/DocumentUpload/DocumentGroup';

const MAX_SIZE = 35;
const MAX_FILE_SIZE = 20;

const DocumentationScreen = ({ navigator }: ScreenProps) => {
    const { values, setFieldValue } = useFormikContext<GenericObject>();
    const [currentStep, { goForward, goBack }] = navigator;
    const [fileRequired, setFileRequired] = useState(false);
    const [errorFileExceedsAllowedSize, setFileExceedsAllowedSize] = useState('');
    const [errorFileOverLimitSize, setFileOverLimitSize] = useState('');

    const id = values.tempID;
    const docList = values.uploadDocList;

    const data = prepareDocumentUploadFormData({ id, documents: docList });
    const { response: documentsList, fetchMethod: getDocuments } = useApi(() => getDocList(id));
    const { response: documents, fetchMethod: postDocuments } = useApi(() => submitDocuments(data), goForward);

    const uploadedFiles = docList.map((file: GenericObject) => file.documentId);
    const requiredFieldsIds = documentsList.data.data?.filter((file: GenericObject) => {
        return file.obavezan === true && !uploadedFiles.includes(file.id);
    });

    useEffect(() => {
        if (values.tempID && values.tempID !== '') getDocuments();
    }, [values.tempID]);

    const handleNext = (): void => {
        documentsList.data.data?.map((file: GenericObject) => file.obavezan === true && setFileRequired(true));
        if (requiredFieldsIds.length === 0) {
            setFieldValue('loadingMessage', translations.documentation.LOADING_MESSAGE);
            postDocuments();
        }
    };

    const handleGoBack = (): void => {
        setFieldValue('submittedSuccessfully', false);
        setFieldValue('errorMsgText', null);
        goBack();
    };

    const addFilesToDocList = async (field: string, event: any, documentId: number) => {
        setFileRequired(false);
        setFileExceedsAllowedSize('');
        setFileOverLimitSize('');
        setFieldValue('errorMsgText', null);

        documents.isLoading = true;
        setFieldValue('loadingMessage', translations.documentation.COMPRESS_MESSAGE);
        const result = await addFilesToDocumentList(docList, MAX_FILE_SIZE, MAX_SIZE, field, event, documentId);
        if (result.valid) {
            setFieldValue('uploadDocList', result.uploadDocList);
        }
        event.target.value = null;
        documents.isLoading = false;

        setFieldValue('errorMsgText', result.errorMsgText);
        setFileExceedsAllowedSize(result.errorFileExceedsAllowedSize ?? '');
        setFileOverLimitSize(result.errorFileOverLimitSize ?? '');
    };

    const deleteUploadedFile = (option: string) => {
        const resultUploadDocList = docList.filter((item: { field: string; value: File }) => {
            const pom = item.value.name !== option;
            return pom;
        });
        setFieldValue('uploadDocList', resultUploadDocList);
        setFieldValue('errorMsgText', null);
        setFileExceedsAllowedSize('');
        setFileOverLimitSize('');
    };

    const renderUploadDoc = (item: any) => {
        return (
            <>
                <DocumentGroup
                    uploadedDocList={docList}
                    group={item}
                    addFilesHandler={addFilesToDocList}
                    deleteFileHandler={deleteUploadedFile}
                />
                {!!item.obavezan &&
                requiredFieldsIds.filter((doc: GenericObject) => doc.id === item.id).length !== 0 &&
                !!fileRequired ? (
                    <FormHelperText error={true}>
                        <>{translations.validationMessages.REQUIRED_FIELD}</>
                    </FormHelperText>
                ) : null}
            </>
        );
    };

    return (
        <>
            <StepWrapper>
                <Loader openLoader={documents.isLoading} message={values.loadingMessage}></Loader>
                <StyledCardSection>
                    <Typography style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: 0 }} variant="caption">
                        {translations.documentation.ADD_DOC}
                    </Typography>

                    <div className="allowed-files-container">
                        <Typography style={{ display: 'flex', justifyContent: 'center' }}>
                            {translations.documentation.TOTAL_UPLOAD_SIZE_LABEL}
                            {MAX_SIZE}
                            {translations.documentation.DOCUMENTATION_MB_SPACE}
                        </Typography>
                        <Typography style={{ display: 'flex', justifyContent: 'center' }}>
                            {translations.documentation.TOTAL_UPLOAD_SIZE_LEFT_LABEL}
                            {Math.floor(MAX_SIZE - totalUploadSize(docList))}
                            {translations.documentation.DOCUMENTATION_MB_SPACE}
                        </Typography>
                        <Typography style={{ display: 'flex', justifyContent: 'center' }}>
                            {translations.documentation.ALLOWED_FILE_FORMATS} {acceptedUploadFileTypes}
                        </Typography>
                    </div>

                    {documentsList.isSuccess && documentsList.data.data.map((item: any) => renderUploadDoc(item))}
                    {values.errorMsgText && (
                        <p style={{ display: 'flex', justifyContent: 'center', color: '#c21b17' }} id="errorMsgText">
                            {values.errorMsgText}
                        </p>
                    )}
                    {errorFileExceedsAllowedSize !== '' && (
                        <p style={{ display: 'flex', justifyContent: 'center', color: '#c21b17' }}>
                            {errorFileExceedsAllowedSize}
                        </p>
                    )}
                    {errorFileOverLimitSize !== '' && (
                        <p style={{ display: 'flex', justifyContent: 'center', color: '#c21b17' }}>
                            {errorFileOverLimitSize}
                        </p>
                    )}
                </StyledCardSection>

                {!documents.isLoading ? (
                    <StepperControls
                        onPrevious={handleGoBack}
                        onNext={handleNext}
                        onNextLabel={common.NEXT}
                        idNext="documentationScreenNext"
                        idPrevious="documentationScreenPrevious"
                    />
                ) : (
                    <></>
                )}
            </StepWrapper>
        </>
    );
};

export default DocumentationScreen;
